import { useState } from 'react';
import type { UiConfig } from '../types';

type Moment = NonNullable<UiConfig['moments']>[number];

interface Props {
  moments: Moment[];
  title?: string;
}

/**
 * CustomerReviews — the preview page's "WHAT CUSTOMERS SAY" block.
 *
 * Per-product reviews sourced from ui_config.moments ({ name, review }).
 * Collapsible white card holding a masonry grid of gold review cards (5 stars
 * + quote + name), matching the template-woo bf-social-proof reviews grid.
 * Renders nothing when the product has no moments.
 */
export function CustomerReviews({ moments, title = 'What customers say' }: Props) {
  const items = moments.filter((m) => m && m.review);
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;

  return (
    <section className="rounded-[18px] bg-white border border-soft-border shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left"
      >
        <span className="text-[13px] font-semibold tracking-[0.12em] uppercase text-warm-brown">{title}</span>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          className={['text-warm-brown/70 transition-transform', open ? 'rotate-180' : ''].join(' ')}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="px-5 sm:px-6 pb-6 columns-1 sm:columns-2 lg:columns-3 gap-4">
          {items.map((m, i) => (
            <figure
              key={`${m.name}-${i}`}
              className="break-inside-avoid mb-4 rounded-[12px] bg-teal p-4"
              style={{ color: '#fff' }}
            >
              <div aria-hidden="true" className="text-sm tracking-[0.15em] mb-2" style={{ color: '#fcd34d' }}>★★★★★</div>
              <blockquote className="text-[13px] leading-relaxed italic m-0" style={{ color: '#fff' }}>“{m.review}”</blockquote>
              {m.name && <figcaption className="mt-2 text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.9)' }}>— {m.name}</figcaption>}
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}
